import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { LogoMark, Wordmark } from "./Logo";
import { NAV_LINKS, RESOURCES_NAV, SERVICES, CONTACT } from "../data/content";

export default function Footer() {
    const year = new Date().getFullYear();
    return (
        <footer className="bg-[#111318] border-t border-white/10" data-testid="site-footer">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                <div>
                    <Link to="/" className="flex items-center gap-3" data-testid="footer-logo-link">
                        <LogoMark className="h-12 w-12" />
                        <Wordmark withTagline />
                    </Link>
                    <p className="text-white/50 text-sm leading-relaxed mt-6 max-w-xs">
                        Professional U.S. tax preparation for individuals, families, and the businesses that employ them.
                    </p>
                </div>

                <div>
                    <p className="eyebrow-dark">Pages</p>
                    <ul className="mt-6 space-y-3">
                        {[...NAV_LINKS, ...RESOURCES_NAV.children].map((l) => (
                            <li key={l.path}>
                                <Link to={l.path} data-testid={`footer-link-${l.slug}`} className="text-white/60 hover:text-[#C8A45D] text-sm transition-colors duration-200 inline-block py-2.5 leading-6">
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <p className="eyebrow-dark">Services</p>
                    <ul className="mt-6 space-y-3">
                        {SERVICES.map((s) => (
                            <li key={s.slug}>
                                <Link to={s.slug === "amended" ? "/tax-amendment" : "/services"} data-testid={`footer-service-${s.slug}`} className="text-white/60 hover:text-[#C8A45D] text-sm transition-colors duration-200 inline-block py-2.5 leading-6">
                                    {s.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <p className="eyebrow-dark">Contact</p>
                    <ul className="mt-6 space-y-4">
                        {CONTACT.phoneHref && (
                            <li>
                                <a href={CONTACT.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 text-white/70 hover:text-[#C8A45D] text-sm transition-colors duration-200">
                                    <Phone className="h-4 w-4 text-[#C8A45D]" /> {CONTACT.phoneDisplay}
                                </a>
                            </li>
                        )}
                        <li>
                            <a href={CONTACT.emailHref} data-testid="footer-email-link" className="flex items-center gap-3 text-white/70 hover:text-[#C8A45D] text-sm transition-colors duration-200">
                                <Mail className="h-4 w-4 text-[#C8A45D]" /> {CONTACT.email}
                            </a>
                        </li>
                        <li>
                            <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer" data-testid="footer-address-link" className="flex items-start gap-3 text-white/70 hover:text-[#C8A45D] text-sm transition-colors duration-200">
                                <MapPin className="h-4 w-4 text-[#C8A45D] mt-0.5 shrink-0" />
                                <span>
                                    <span className="block text-[11px] uppercase tracking-[0.18em] text-white/45">Our Location</span>
                                    <span className="block mt-0.5">{CONTACT.address}</span>
                                </span>
                            </a>
                        </li>
                        <li>
                            <Link to="/contact" data-testid="footer-inquiry-link" className="inline-block mt-2 text-[13px] uppercase tracking-[0.12em] text-[#C8A45D] border-b border-[#C8A45D]/40 pb-1 hover:border-[#C8A45D] transition-colors duration-200">
                                Start an Inquiry
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-6 pb-24 sm:pb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <p className="text-white/40 text-xs">© 2017–{year} Freedom Tax Filer. All rights reserved.</p>
                    <div className="flex items-center gap-5" data-testid="footer-legal-links">
                        <Link to="/privacy" data-testid="footer-privacy-link" className="text-white/40 text-xs hover:text-[#C8A45D] transition-colors duration-200">Privacy Policy</Link>
                        <Link to="/terms" data-testid="footer-terms-link" className="text-white/40 text-xs hover:text-[#C8A45D] transition-colors duration-200">Terms of Service</Link>
                    </div>
                    <p className="text-white/30 text-xs max-w-xl">
                        Freedom Tax Filer provides tax preparation services. This website is for general information and inquiries — it is not legal or financial advice.
                    </p>
                </div>
            </div>
        </footer>
    );
}
